//Library Imports
import { shallowEqual, useSelector } from "react-redux";
import store, { RootState } from "store";
import { DatePicker } from "antd";
import React, { useState, useEffect } from "react";

//Local Imports
import Search from "../../Content/Search/Components/Search";
import SearchTab from "../../Content/Search/Components/SearchTab";
import ToggleView from "../../Content/Shared/ToggleView";
import { getAllTimeDates, getDay, getMonthDates, getQuarterDates, getWeekDates } from "utils";
import EditItemName from "modules/Common/Components/EditItemName";
import AssetsTab from "../Emails/Components/AssetsTab";
import Checkbox from "modules/Common/Components/Checkbox";
import SelectWithIcon from "./SelectWithIcon";
import { ReactComponent as Sort } from "../../../assets/img/icons/Sort.svg";
import { ReactComponent as Calender } from "../../../assets/img/icons/Campaign/Calender.svg";
import { TAB_TYPES } from "../../../constants/types/tabTypes";
import AddIcon from "../../../assets/img/icons/addicon.svg";
import { TAB_CAMPAIGNS } from "constants/paths";
import { getQueryCampaignId } from "modules/EmailBrowser/utils";
import { getSearchArticles } from "modules/Content/Search/actions";
import { IArticlesItem } from "modules/Content/interfaces";
import SelectedBottom from "modules/Common/Components/SelectedBottom";
import { removeSelectedIds } from "modules/Content/DragnDrop/actions";
import { closeOverlay, openOverlay } from "modules/GlobalActions";
import { SEARCH_ARTICLES } from "constants/actionTypes";
import { getCampaignDetails } from "../Emails/actions";
import FloatingSidebarTwo from "modules/Common/Components/FloatingSidebarTwo";
import AdvancedSearch from "modules/Content/Shared/AdvancedSearch";

//Style Imports
import styled from "styled-components";

interface ISmartSourceContent {
  tab?: string;
  page?: string;
  searchTerms: string[];
  title?: string;
  tabType?: string;
  hideHeader?: boolean;
  noKeywords?: boolean;
  hideFocusMessage?: boolean;
  isAssetWithoutCampaign?: boolean;
  assetWithoutCampaignName?: string;
  assetWithoutCampaignId?: string;
  hideCMS?: boolean;
  savedKeywords?: string[];
  isCampaign?: boolean;
  inEditionSmartSourced?: boolean;
  description?: string;
}

const SmartSourceContent: React.FC<ISmartSourceContent> = (props) => {
  const [sortBy, setSortBy] = useState(1);
  const [dateFilter, setDateFilter] = useState(getDates("ALL TIME"));
  const [allSearchTerms, setAllSearchTerms] = useState(props.searchTerms);
  const [isListView, setIsListView] = useState(localStorage.getItem("isListView") === "true");
  const advancedSearchCheck = useSelector((state: RootState) => state.uiReducer.advancedSearchCheck);
  const sourcedContent = useSelector((state: RootState) => state.searchArticles.sourcedContent);
  const excludedContent = useSelector((state: RootState) => state.searchArticles.excludedContent);
  const excludedKeywords = useSelector((state: RootState) => state.searchArticles.excludedKeywords);
  const includedKeywords = useSelector((state: RootState) => state.searchArticles.includedKeywords);

  const FIRST_PAGE_LIMIT = 11;
  const DEFAULT_PAGE_LIMIT = 21;

  const {
    tab,
    page,
    title,
    tabType,
    hideHeader,
    noKeywords,
    hideFocusMessage,
    isAssetWithoutCampaign,
    hideCMS,
    savedKeywords,
    isCampaign,
    inEditionSmartSourced,
    description,
  } = props;

  const [isEditable, setIsEditable] = useState(false);
  const articlesCount: number = useSelector((state: RootState) => state.searchArticles.articlesCount);
  const articles: IArticlesItem[] = useSelector((state: RootState) => state.searchArticles.data);
  const campaignName = useSelector((state: RootState) => state.emails.campaignName);
  const selectedIds = useSelector((state: RootState) => state.dragDrop.selectedIds);
  const [selectedDateOption, setSelectedDateOption] = useState({ value: "ALL TIME", label: "All Time" });
  const [selectedDatesObj, setSelectedDatesObj] = useState({ start_date: "", end_date: "" });

  const isAssetLinkedToCampaignFromRedux: any = useSelector(
    (state: RootState) => state.campaignDataForAsset.data.campaignId,
    shallowEqual
  );

  const showAdvancedSearchSidebar: boolean = useSelector(
    (state: RootState) => state.uiReducer.showAdvancedSearchSidebar
  );

  const setSearchTerms = (searchTerms: string[]) => {
    setAllSearchTerms(searchTerms);
  };

  const setEditableSearchField = (value: boolean) => {
    setIsEditable(value);
  };
  const handleToggle = (value: boolean) => {
    localStorage.setItem("isListView", `${value}`);
    setIsListView(value);
  };

  function getDates(value: string) {
    let datesObject: any = {};
    let type = value.split(" ")[1];
    let day = value.split(" ")[0];

    switch (type) {
      case "TIME":
        datesObject = getAllTimeDates();
        break;

      case "QTR":
        datesObject = getQuarterDates();
        break;

      case "Month":
        datesObject = getMonthDates();
        break;

      case "Weeks":
        datesObject = getWeekDates(14);
        break;

      case "Days":
        datesObject = getDay(Number(day));
        break;

      default:
        break;
    }
    return datesObject;
  };

  // value argument for getFilteredContent function can both be an object and string
  //for custom date range it is object the it receives otherwise string
  const getFilteredContent = (filterType: string, value: any) => {
    let dates = dateFilter;
    let sort = sortBy;
    if (filterType === "content-sort") {
      sort = Number(value);
      setSortBy(Number(value));
    } else if (typeof value === "object") {
      dates = value.value;
      setDateFilter(dates);
    } else {
      dates = getDates(value);
      setDateFilter(dates);
      setSelectedDatesObj({ start_date: "", end_date: "" });
    }
    store.dispatch({
      type: SEARCH_ARTICLES.RESET_TOTAL_COUNT,
    });
    if (advancedSearchCheck) {
      getSearchArticles(
        [],
        0,
        articles.length < 1 ? FIRST_PAGE_LIMIT : DEFAULT_PAGE_LIMIT,
        sort,
        dates,
        false,
        advancedSearchCheck,
        sourcedContent,
        excludedContent,
        excludedKeywords,
        includedKeywords
      );
    } else {
      getSearchArticles(
        allSearchTerms,
        0,
        articles.length < 1 ? FIRST_PAGE_LIMIT : DEFAULT_PAGE_LIMIT,
        sort,
        dates,
        false
      );
    }
  };

  useEffect(() => {
    closeOverlay();
    if (props.searchTerms.length <= 0) {
    } else {
      store.dispatch({
        type: SEARCH_ARTICLES.RESET_TOTAL_COUNT,
      });
    }
    if (isAssetLinkedToCampaignFromRedux) {
      localStorage.setItem("campaign_id", isAssetLinkedToCampaignFromRedux);
    }
  }, [isAssetLinkedToCampaignFromRedux]);

  useEffect(() => {
    if (advancedSearchCheck) {
      if (allSearchTerms.length > 0) {
        setSearchTerms([]);
      }
    }
  }, [advancedSearchCheck]);

  useEffect(() => {
    if (allSearchTerms.length === 0) {
      setAllSearchTerms(props.searchTerms);
    }
  }, [props.searchTerms]);

  useEffect(() => {
    if (getQueryCampaignId()) {
      getCampaignDetails();
    }
    store.dispatch({
      type: SEARCH_ARTICLES.RESET_TOTAL_COUNT,
    });
    return () => { };
  }, []);

  useEffect(() => {
    if (showAdvancedSearchSidebar) openOverlay();
    else closeOverlay();
  }, [showAdvancedSearchSidebar]);

  return (
    <Wrapper>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {!hideHeader && tabType === TAB_TYPES.ASSET && (
          <div>
            <EditItemName itemName={title ? title : ""} itemTitle="Digest" />
            <div className="margin-top-md margin-bottom-md">
              <AssetsTab tab={TAB_CAMPAIGNS.SOURCED_CONTENT} />
            </div>
          </div>
        )}
        {inEditionSmartSourced && (
          <div className="styling-heading-text">
            Click {<img style={{ height: 22, width: 22 }} src={AddIcon} alt="add icon" />} to curate to this email
          </div>
        )}
        <Search
          onSearch={setSearchTerms}
          onEdit={setEditableSearchField}
          editable={true}
          noKeywords={noKeywords}
          smartSearchTerms={allSearchTerms}
          savedKeywords={savedKeywords}
          hideFocusMessage={hideFocusMessage}
          isAssetWithoutCampaign={isAssetWithoutCampaign}
          sortBy={sortBy}
          dateFilter={dateFilter}
          setSelectedDatesObj={setSelectedDatesObj}
        />
        <div className="tabs-wrapper d-flex padding-top-lg padding-bottom-sm">
          <ul className="d-flex justify-content-between w-100">
            <li>
              <ul>
                <li>
                  <ToggleView isListView={isListView} onToggle={handleToggle} />
                </li>
                {hideCMS ? null : (
                  <>
                    {tab != "source" && tabType != "source" && (
                      <div className="check-container">
                        <li className="d-flex align-items-center">
                          <label className="d-flex align-items-center">
                            <Checkbox />
                            <span className="padding-bottom-checkbox-text">Show My CMS content</span>
                          </label>
                        </li>
                        <li className="d-flex align-items-center">
                          <label className="d-flex align-items-center">
                            <Checkbox />
                            <span className="padding-bottom-checkbox-text">Show Theme content</span>
                          </label>
                        </li>
                      </div>
                    )}
                  </>
                )}
              </ul>
            </li>
            <li>
              <ul id="Filters-SmartSourced">
                <li className="right">
                  <SelectWithIcon
                    emoji={<Sort style={{ height: 18, width: 18 }} />}
                    menuWidth={160}
                    defaultValue={{ value: "Relevance", label: "Relevance" }}
                    options={[
                      { value: "1", label: "Relevance" },
                      { value: "2", label: "Most Recent" },
                    ]}
                    onChange={(e) => {
                      // added this check so that API called don't dispatch if there is no keywords i.e not in search bar and in advance search
                      if (allSearchTerms.length || includedKeywords.length) {
                        getFilteredContent("content-sort", e.value);
                      }
                    }}
                  />
                </li>
                <li className="right">
                  <SelectWithIcon
                    value={selectedDateOption}
                    emoji={<Calender style={{ height: 18, width: 18 }} />}
                    menuWidth={300}
                    defaultValue={{ value: "7 Days", label: "Last 7 Days" }}
                    options={[
                      { value: "2 Days", label: "Last 2 Days" },
                      { value: "7 Days", label: "Last 7 Days" },
                      { value: "2 Weeks", label: "2 Weeks" },
                      { value: "1 Month", label: "1 Month" },
                      { value: "1 QTR", label: "1 QTR" },
                      { value: "ALL TIME", label: "All Time" },
                      { label: "Custom", value: "custom" },
                    ]}
                    isSmartSource
                    datesObj={selectedDatesObj}
                    setDatesObj={setSelectedDatesObj}
                    dateFilter={selectedDateOption}
                    setDateFilter={setSelectedDateOption}
                    onChange={(e) => {
                      // added this check so that API called don't dispatch if there is no keywords i.e not in search bar and in advance search
                      if (allSearchTerms.length || includedKeywords.length) {
                        getFilteredContent("date", e.value);
                      }
                    }}
                    customDatePicker={<DatePicker bordered={false} />}
                  />
                </li>
                <li className="right">
                  <div className="results">{articlesCount.toLocaleString()} results</div>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>

      <div className="tabs-content-wrapper padding-bottom-lg">
        <SearchTab
          isCampaign={isCampaign}
          searchTerms={allSearchTerms}
          isListView={isListView}
          sortBy={sortBy}
          dateFilter={dateFilter}
          inEditionSmartSourced={inEditionSmartSourced}
          description={description}
        />
      </div>
      <SelectedBottom
        hideAction={true}
        selectedCount={selectedIds?.length}
        selectedMessage={` ${selectedIds.length === 1 ? "article" : "articles"} selected`}
        onClearSelection={removeSelectedIds}
      />
      <FloatingSidebarTwo
        isToggle={showAdvancedSearchSidebar}
        advancedSearch={true}
        inCampaignSmartSourced={true}
        className="detail-person-view"
      >
        <AdvancedSearch inCampaignSmartSourced={true} />
      </FloatingSidebarTwo>
    </Wrapper>
  );
};

export default SmartSourceContent;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  // padding: 70px 90px 0 90px;
  // @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25){
  //    padding: 35px 50px 0 50px;
  // }
  .styling-heading-text {
    color: #424b50;
    font-size: 32px;
    font-weight: 600;
    min-width: 500px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 22px;
    }
  }
  .search-hint {
    display: none;
  }
  .tabs-wrapper {
    justify-content: space-between;
    align-items: center;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding-bottom: 12px !important;
    }

    ul {
      list-style-type: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-wrap: wrap;
      li {
        margin: 5px 0;
        .padding-bottom-checkbox-text {
          padding-bottom: 3px;
          color: rgb(126, 126, 126);
          font-size: 16px;
          font-family: "Roboto Slab", serif;
          font-weight: 500;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 12px;
          }
        }
        li {
          margin: 0 5px;
        }
      }
    }
    p.articles-count {
      margin-bottom: 0px;
      color: rgb(126, 126, 126);
      font-size: 14px;
      font-weight: 600;
      font-family: "robotomedium";
      white-space: nowrap;
      display: inline-block;
      float: right;
    }
  }

  .tabs-content-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    div[role="DraggableBox"] {
      height: 100%;
      div[role="Box"] {
        height: 100%;
      }
    }
    .list-style {
      div[role="DraggableBox"] {
        height: auto;
        div[role="Box"] {
          height: auto;
        }
      }
    }
    .small-three {
      height: 100%;
      flex-direction: column;
      display: flex;
      justify-content: start;
      align-items: center;
      flex-wrap: wrap;
      &:not(.small-two-only) {
        div[role="DraggableBox"]:nth-of-type(2) {
          margin-top: 15px;
        }
      }
      div[role="DraggableBox"] {
        height: auto;
        div[role="Box"] {
          height: auto;
        }
      }
      &.small-two-only {
        justify-content: space-between;
      }
    }
  }
  h1 {
    color: #424b50;
    font-size: 40px;
    font-weight: 400;
    line-height: 47px;
    margin-bottom: 17px;
    display: block;
    font-family: "Roboto Slab", serif;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 28px;
      line-height: 32px;
      margin-bottom: 10px;
    }
  }
  .message-wrapper {
    margin-bottom: 5%;
  }
  .check-container {
    display: flex;
    li:hover,
    span:hover {
      cursor: pointer;
    }
  }
  .results {
    color: #7e7e7e;
    font-family: "Roboto Slab", serif;
    font-size: 14px;
    font-weight: 500;
    padding-top: 9px;
    margin-left: 5px;
    min-width: 115px;
    text-align: right;
  }
  #Filters-SmartSourced {
    li {
      min-width: 173px;
    }
    li:last-child {
      min-width: unset;
    }
  }
`;
